import { useEffect } from "react";
import "@/lib/posthog";
import { PostHogErrorBoundary } from "@/components/posthog-error-boundary";
import { Outlet, createRootRoute, HeadContent, Scripts, useRouterState } from "@tanstack/react-router";
import { hasAnalyticsConsent } from "@/lib/cookie-consent";

import { crsPageView, installDelegatedClickTracking } from "@/lib/analytics-events";
import { CookieConsentBanner } from "@/components/cookie-consent-banner";
import appCss from "../styles.css?url";
import interLatinWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { FinanceProviderDisclosure } from "@/components/finance-provider-disclosure";
import { LpFunnelFooter } from "@/components/lp-funnel-footer";
import { shouldShowCompactFinanceDisclosure } from "@/lib/finance-disclosure-pages";
import { getFooterMode, shouldShowMobileBottomStickyCta } from "@/lib/nav-config";
import { SITE, COVERAGE_AREAS } from "@/lib/site";
import { SCHEMA_AREA_SERVED } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { FinancePostcodeProvider } from "@/context/finance-postcode-context";

const ROUTES_WITHOUT_DEFAULT_CHROME = new Set<string>([
  "/damp-survey",
  "/solar-savings-calculator",
  "/design-solar",
]);

function NotFoundComponent() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-primary">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-full bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground hover:bg-accent/90"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}




export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "Mf4VqzmfcVAQXVi6hCbp6bvSeYwq3FfXMmEyxshWEuk" },
      { name: "google-site-verification", content: "QO-Xz6soyV8p3fpp1j-nQvhU0uuKQ5Da8bfOAmxkq_M" },
      { name: "author", content: SITE.legalName },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "format-detection", content: "telephone=yes" },
      { name: "theme-color", content: "#0b3a66" },
      // Local SEO / geo targeting
      { name: "geo.region", content: SITE.geo.region },
      { name: "geo.placename", content: SITE.geo.placename },
      { name: "geo.position", content: `${SITE.geo.latitude};${SITE.geo.longitude}` },
      { name: "ICBM", content: `${SITE.geo.latitude}, ${SITE.geo.longitude}` },
      // Open Graph sitewide defaults (per-route head() overrides title/description/image)
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE.name },
      { property: "og:locale", content: "en_GB" },
      // Twitter sitewide defaults
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "preload",
        as: "font",
        href: interLatinWoff2,
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      { rel: "icon", href: "/favicon.ico" },
    ],
    scripts: [
      {
        type: "text/javascript",
        children: `(function(){
  try {
    var loc = window.location;
    var ua = navigator.userAgent || '';
    var host = loc.hostname || '';
    var search = loc.search || '';
    var isLovableParam = /[?&]__lovable_/.test(search);
    var isLovableHost = /(^|\\.)lovable\\.(app|dev)$/i.test(host) || host === 'localhost' || host === '127.0.0.1';
    var isBot = /(HeadlessChrome|Lighthouse|Chrome-Lighthouse|GTmetrix|PTST|PageSpeed|Googlebot|bingbot|AhrefsBot|SemrushBot)/i.test(ua);
    if (isLovableParam || isLovableHost || isBot) {
      window.__crsAnalyticsDisabled = true;
      return;
    }
    // Sanitised page path (strip any __lovable_* params if present)
    var params = new URLSearchParams(search);
    var toDelete = [];
    params.forEach(function(_, k){ if (k.indexOf('__lovable_') === 0) toDelete.push(k); });
    toDelete.forEach(function(k){ params.delete(k); });
    var qs = params.toString();
    var sanitizedPath = loc.pathname + (qs ? '?' + qs : '');

    // Microsoft UET Consent Mode default (must be set before the tag loads)
    window.uetq = window.uetq || [];
    window.uetq.push('consent', 'default', { 'ad_storage': 'denied' });

    // Defer analytics/tag scripts off the critical path (helps LCP/INP).
    var loaded = false;
    function loadAnalytics(){
      if (loaded) return; loaded = true;


    var params = new URLSearchParams(search);
    var toDelete = [];
    params.forEach(function(_, k){ if (k.indexOf('__lovable_') === 0) toDelete.push(k); });
    toDelete.forEach(function(k){ params.delete(k); });
    var qs = params.toString();
    var sanitizedPath = loc.pathname + (qs ? '?' + qs : '');

    // GA4
    var ga = document.createElement('script');
    ga.async = true;
    ga.src = 'https://www.googletagmanager.com/gtag/js?id=G-QYM4V3JDP7';
    document.head.appendChild(ga);
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('set', { page_path: sanitizedPath });
    gtag('config', 'G-QYM4V3JDP7', { page_path: sanitizedPath });

    // Microsoft Clarity
    (function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "xpvh1zcwe9");

    // Microsoft Advertising UET
    (function(w, d, t, u, o) {
      w[u] = w[u] || [], o.ts = (new Date).getTime();
      var n = d.createElement(t);
      n.src = "https://bat.bing.net/bat.js?ti=" + o.ti + ("uetq" != u ? "&q=" + u : "");
      n.async = 1;
      n.onload = n.onreadystatechange = function() {
        var s = this.readyState;
        s && "loaded" !== s && "complete" !== s || (o.q = w[u], w[u] = new UET(o), w[u].push("pageLoad"), n.onload = n.onreadystatechange = null);
      };
      var i = d.getElementsByTagName(t)[0];
      i.parentNode.insertBefore(n, i);
    })(window, document, "script", "uetq", { ti: "343264644", enableAutoSpaTracking: true });

    try {
      if (localStorage.getItem('crs_cookie_consent') === 'accepted') {
        window.uetq = window.uetq || [];
        window.uetq.push('consent', 'update', { 'ad_storage': 'granted' });
      }
    } catch (e) { /* no-op */ }

    // GoHighLevel external tracking
    var ghl = document.createElement('script');
    ghl.async = true;
    ghl.src = 'https://link.msgsndr.com/js/external-tracking.js';
    ghl.setAttribute('data-tracking-id', 'tk_c6fda5e65c13429cbbc9cd539601f183');
    document.head.appendChild(ghl);

    // Meta (Facebook) Pixel
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document, 'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '1367724381572743');
    fbq('track', 'PageView');
    }


    var events = ['pointerdown','keydown','scroll','touchstart'];
    function trigger(){ events.forEach(function(ev){ window.removeEventListener(ev, trigger, { passive: true, capture: true }); }); loadAnalytics(); }
    events.forEach(function(ev){ window.addEventListener(ev, trigger, { passive: true, capture: true }); });
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadAnalytics, { timeout: 4000 });
    } else {
      setTimeout(loadAnalytics, 3000);
    }
  } catch (e) { /* no-op */ }
})();`,
      },


      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "HomeAndConstructionBusiness",
              "@id": `${SITE.url}/#organization`,
              name: SITE.name,
              legalName: SITE.legalName,
              alternateName: "Complete Remedial Solutions",
              url: SITE.url,
              logo: `${SITE.url}/og-default.jpg`,
              email: SITE.email,
              telephone: "+448000092992",
              foundingDate: "2018-09-21",
              identifier: {
                "@type": "PropertyValue",
                name: "Companies House Number",
                value: "11582758",
              },
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  telephone: "+448000092992",
                  contactType: "customer service",
                  areaServed: "GB",
                },
                {
                  "@type": "ContactPoint",
                  telephone: "+441656253194",
                  contactType: "customer service",
                  areaServed: "GB",
                },
              ],
              hasCredential: [
                {
                  "@type": "EducationalOccupationalCredential",
                  credentialCategory: "certification",
                  name: "MCS Certified Installer - Certificate No. IAA-10295",
                },
                {
                  "@type": "EducationalOccupationalCredential",
                  credentialCategory: "membership",
                  name: "HIES Consumer Code Member - Membership No. CRS/A/1864",
                },
                {
                  "@type": "EducationalOccupationalCredential",
                  credentialCategory: "registration",
                  name: "TrustMark Registered Business - Registration No. 2925406",
                },
              ],
              sameAs: [
                SITE.social.facebook,
                SITE.social.linkedin,
                "https://find-and-update.company-information.service.gov.uk/company/11582758",
              ],
              areaServed: [...SCHEMA_AREA_SERVED],
            },
            {
              "@type": "LocalBusiness",
              "@id": `${SITE.url}/#localbusiness`,
              name: SITE.name,
              legalName: SITE.legalName,
              alternateName: "Complete Remedial Solutions",
              url: SITE.url,
              telephone: "+448000092992",
              email: SITE.email,
              image: `${SITE.url}/og-default.jpg`,
              priceRange: "££",
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  telephone: "+448000092992",
                  contactType: "customer service",
                  areaServed: "GB",
                },
                {
                  "@type": "ContactPoint",
                  telephone: "+441656253194",
                  contactType: "customer service",
                  areaServed: "GB",
                },
              ],
              sameAs: [
                SITE.social.facebook,
                SITE.social.linkedin,
                "https://find-and-update.company-information.service.gov.uk/company/11582758",
              ],
              address: {
                "@type": "PostalAddress",
                streetAddress: `${SITE.address.line1}, ${SITE.address.line2}`,
                addressLocality: SITE.address.city,
                postalCode: SITE.address.postcode,
                addressCountry: "GB",
              },
              geo: {
                "@type": "GeoCoordinates",
                latitude: SITE.geo.latitude,
                longitude: SITE.geo.longitude,
              },
              openingHoursSpecification: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  opens: "08:00",
                  closes: "17:00",
                },
              ],
              areaServed: COVERAGE_AREAS.map((name) => ({ "@type": "City", name })),
            },
            {
              "@type": "WebSite",
              "@id": `${SITE.url}/#website`,
              url: SITE.url,
              name: SITE.name,
              publisher: { "@id": `${SITE.url}/#organization` },
              inLanguage: "en-GB",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en-GB">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <CookieBannerGate />
        <Scripts />
      </body>
    </html>
  );
}

/** The calculator wizard is a full-screen funnel, so the banner is hidden there. */
function CookieBannerGate() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (pathname.startsWith("/solar-savings-calculator") || pathname.startsWith("/s/")) return null;
  return <CookieConsentBanner />;
}


function SiteFooterForPath({ pathname }: { pathname: string }) {
  const footerMode = getFooterMode(pathname);
  if (footerMode === "funnel") return <LpFunnelFooter />;
  if (footerMode === "minimal") return null;
  return <SiteFooter />;
}

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locationKey = useRouterState({ select: (s) => s.location.href });
  const skipChrome = ROUTES_WITHOUT_DEFAULT_CHROME.has(pathname);
  const showMobileStickyCta = shouldShowMobileBottomStickyCta(pathname);

  // Install delegated click tracking once (idempotent).
  useEffect(() => {
    installDelegatedClickTracking();
  }, []);

  // Standardised page view for both initial load and every confirmed
  // client-side navigation. `location.key` changes exactly once per route
  // change (including back/forward), and the crsPageView helper dedupes
  // Strict Mode remounts by that key.
  useEffect(() => {
    crsPageView(locationKey);
  }, [locationKey]);

  useEffect(() => {
    if (!hasAnalyticsConsent()) return;
    // Deferred + code-split: keeps the database client out of the initial bundle.
    const run = () => {
      void import("@/lib/visitor-tracking").then((m) => m.trackPageview(pathname));
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(run);
    } else {
      window.setTimeout(run, 1200);
    }
  }, [pathname]);

  if (skipChrome) {
    return (
      <FinancePostcodeProvider>
        <PostHogErrorBoundary><Outlet /></PostHogErrorBoundary>
      </FinancePostcodeProvider>
    );
  }

  return (
    <FinancePostcodeProvider>
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main
          className={cn(
            "flex-1",
            showMobileStickyCta && "max-lg:pb-[var(--mobile-sticky-cta-height)]",
          )}
        >
          <PostHogErrorBoundary><Outlet /></PostHogErrorBoundary>
        </main>
        {shouldShowCompactFinanceDisclosure(pathname) && (
          <div className="border-t border-border/50 bg-secondary/30">
            <div className="container-page py-6 md:py-8 max-w-4xl">
              <FinanceProviderDisclosure variant="compact" />
            </div>
          </div>
        )}
        <SiteFooterForPath pathname={pathname} />
      </div>
    </FinancePostcodeProvider>
  );
}
